// src/components/NotificationModule.jsx
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import {
  Badge,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Box,
  Button,
  TextField,
  Paper,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import Echo from "laravel-echo";
import Pusher from "pusher-js";
import api from "../api/api"; // ✅ unified axios instance (dual storage + refresh)
import { useSelector } from "react-redux";

/* ------------------------------------------------------------------
 * 1) Env helper (Vite or CRA)
 * ----------------------------------------------------------------*/
const env = (key, fallback = undefined) =>
  (typeof import.meta !== "undefined" && import.meta.env && import.meta.env[key]) ||
  process.env[key] ||
  fallback;

/* API base with /api suffix (same as your axios baseURL) */
const API_BASE =
  env("VITE_API_URL", env("REACT_APP_API_URL", "http://localhost:8000/api"));

/* ------------------------------------------------------------------
 * 2) Token helpers (support both storages)
 * ----------------------------------------------------------------*/
const readAccessToken = () =>
  localStorage.getItem("access_token") || sessionStorage.getItem("access_token") || null;

/* ------------------------------------------------------------------
 * 3) Echo singleton re-created when token/user changes
 * ----------------------------------------------------------------*/
window.Pusher = Pusher;

function buildEcho(token) {
  const key = env("VITE_PUSHER_APP_KEY", env("REACT_APP_PUSHER_APP_KEY"));
  const cluster = env("VITE_PUSHER_APP_CLUSTER", env("REACT_APP_PUSHER_APP_CLUSTER", "eu"));
  const host = env("VITE_PUSHER_HOST", env("REACT_APP_PUSHER_HOST", ""));
  const port = Number(env("VITE_PUSHER_PORT", env("REACT_APP_PUSHER_PORT", "6001")));
  const scheme = env("VITE_PUSHER_SCHEME", env("REACT_APP_PUSHER_SCHEME", "http"));

  if (!key) {
    console.error("❌ Pusher key missing (VITE_PUSHER_APP_KEY / REACT_APP_PUSHER_APP_KEY).");
    return null;
  }
    return null;
  // todo remove, this is to stop the console spam on localhost
  return null;

  return new Echo({
    broadcaster: "pusher",
    key,
    cluster,
    wsHost: host || (scheme === "https" ? `ws-${cluster}.pusher.com` : "localhost"),
    wsPort: port,
    wssPort: port,
    forceTLS: scheme === "https",
    disableStats: true,

    // ✅ Hit Laravel API, not the front-end origin
    authEndpoint: `${API_BASE}/broadcasting/auth`,

    // ✅ Always send Bearer; if token rotates we rebuild Echo
    auth: { headers: token ? { Authorization: `Bearer ${token}` } : {} },
  });
}

/* ------------------------------------------------------------------
 * 4) Context
 * ----------------------------------------------------------------*/
const NotificationCtx = createContext({ notifications: [], unread: 0, markAllRead: () => {} });
export const useNotifications = () => useContext(NotificationCtx);

export function NotificationProvider({ children }) {
  const { user, isAuthenticated } = useSelector((s) => s.auth);

  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);

  // Track token (updates if it appears later or rotates)
  const [token, setToken] = useState(readAccessToken());
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "access_token") setToken(readAccessToken());
    };
    window.addEventListener("storage", onStorage);
    const id = setInterval(() => setToken(readAccessToken()), 600); // light poll
    return () => {
      window.removeEventListener("storage", onStorage);
      clearInterval(id);
    };
  }, []);

  /* ── Initial pull (after we have auth) ─────────────────────── */
  useEffect(() => {
    if (!isAuthenticated || !user || !token) return;

    api
      .get("/notifications", { params: { unread: 1 } })
      .then((res) => {
        const rows = res.data?.data ?? res.data ?? [];
        setNotifications(Array.isArray(rows) ? rows : []);
        const total = res.data?.meta?.total;
        setUnread(Number.isFinite(total) ? Number(total) : rows.length);
      })
      .catch((e) => {
        // api.js already handles refresh/redirect; keep quiet here
        console.debug("Notifications fetch error:", e?.message || e);
      });
  }, [isAuthenticated, user, token]);

  /* ── Real-time subscription (rebuild Echo on token/user change) ─ */
  const echoRef = useRef(null);
  useEffect(() => {
    if (!isAuthenticated || !user || !token) return;

    // Dispose previous
    try {
      echoRef.current?.disconnect();
    } catch {}
    echoRef.current = buildEcho(token);
    const e = echoRef.current;
    if (!e) return;

    const channel = e.private(`users.${user.id}`);
    const handler = (notif) => {
      setNotifications((prev) => [notif, ...prev]);
      setUnread((c) => c + 1);
    };
    channel.notification(handler);

    return () => {
      try {
        channel.unsubscribe();
        e.disconnect();
      } catch {}
    };
  }, [isAuthenticated, user?.id, token]);

  /* ── Helpers ───────────────────────────────────────────────── */
  const markAllRead = useCallback(() => {
    if (!token) return;
    api
      .patch("/notifications/mark-all-read")
      .then(() => {
        setUnread(0);
        setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      })
      .catch((e) => console.debug("mark-all-read error:", e?.message || e));
  }, [token]);

  const value = useMemo(() => ({ notifications, unread, markAllRead }), [notifications, unread, markAllRead]);
  return <NotificationCtx.Provider value={value}>{children}</NotificationCtx.Provider>;
}

/* ------------------------------------------------------------------
 * 5) Bell (UI)
 * ----------------------------------------------------------------*/
export function NotificationBell() {
  const { unread, notifications, markAllRead } = useNotifications();
  const [anchorEl, setAnchorEl] = useState(null);

  return (
    <>
      <IconButton color="inherit" size="large" onClick={(e) => setAnchorEl(e.currentTarget)}>
        <Badge badgeContent={unread} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)} sx={{ maxWidth: 320 }}>
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle1">Notifications</Typography>
        </Box>

        {notifications.length === 0 && <MenuItem disabled>No notifications</MenuItem>}

        {notifications.slice(0, 10).map((n) => (
          <MenuItem key={n.id || `${n.title}-${n.created_at}`} divider>
            <Box sx={{ maxWidth: 260 }}>
              <Typography variant="subtitle2">{n.title || n.data?.title || "Notification"}</Typography>
              <Typography variant="body2" color="text.secondary" noWrap>
                {n.message || n.data?.message || ""}
              </Typography>
            </Box>
          </MenuItem>
        ))}

        <Box sx={{ display: "flex", justifyContent: "space-between", p: 1 }}>
          <Button size="small" onClick={markAllRead} disabled={unread === 0}>
            Mark all read
          </Button>
        </Box>
      </Menu>
    </>
  );
}

/* ------------------------------------------------------------------
 * 6) Admin broadcast page (unchanged UI)
 * ----------------------------------------------------------------*/
export function PublishNotificationPage() {
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [ok, setOk] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/notifications", { title, message, broadcast: true });
      setTitle("");
      setMessage("");
      setOk(true);
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper sx={{ p: 3, maxWidth: 600, mx: "auto" }}>
      <Typography variant="h5" gutterBottom>
        Publish Notification
      </Typography>
      <Box component="form" sx={{ display: "flex", flexDirection: "column", gap: 2 }} onSubmit={handleSubmit}>
        <TextField label="Title" required value={title} onChange={(e) => setTitle(e.target.value)} />
        <TextField label="Message" multiline rows={3} value={message} onChange={(e) => setMessage(e.target.value)} />
        <Button variant="contained" type="submit" disabled={saving}>
          {saving ? "Publishing…" : "Publish"}
        </Button>
        {ok && <Typography color="success.main">Notification sent 🎉</Typography>}
      </Box>
    </Paper>
  );
}
